import React, { useState, useTransition } from "react";
import { useTranslation } from "react-i18next";

import { CountryIcon } from "../../assets/icons";
import { LANGUAGES } from "../../data/DemoData";
import useClickOutside from "../../hooks/useClickOutside";
import { LocalisationDropdownProps } from "./types";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  setSelectedCountry,
  setSelectedCurrency,
} from "../../features/store/localeSlice";

export const LocaleDropdown: React.FC<LocalisationDropdownProps> = ({
  setIsOpen,
  localisationBtnRef,
}) => {
  const { i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0],
  );
  const [showLanguageOptions, setShowLanguageOptions] = useState(false);
  const [, startTransition] = useTransition();

  const localisationRef = useClickOutside(
    () => {
      setIsOpen(false);
    },
    true,
    localisationBtnRef,
  );

  const handleLanguageChange = async (language: (typeof LANGUAGES)[0]) => {
    startTransition(() => {
      i18n
        .changeLanguage(language.code)
        .then(() => {
          document.documentElement.dir = language.dir;
          document.documentElement.lang = language.code;
          setSelectedLanguage(language);
          setShowLanguageOptions(false);
        })
        .catch((error) => {
          console.error("Language change failed:", error);
        });
    });
  };

  return (
    <div
      ref={localisationRef}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className="dark:bg-[black] bg-white rounded-lg shadow-md w-80 z-50 p-4 flex flex-col items-center justify-center"
    >
      <CountryDropdown />
      <div className="w-full">
        <h3 className="text-base font-medium mb-4 dark:text-white">
          Localisation
        </h3>

        {/* Language Selection */}
        <div className="mb-4 relative">
          <div
            className="flex items-center justify-between p-2 bp:p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setShowLanguageOptions(!showLanguageOptions);
            }}
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center rounded-full overflow-hidden">
                {selectedLanguage.Flag ? (
                  <selectedLanguage.Flag />
                ) : (
                  <CountryIcon />
                )}
              </div>
              <span className="text-[9px] bp:text-sm lg:text-base dark:text-white">
                {selectedLanguage.name} ({selectedLanguage.country})
              </span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-2 w-2 bp:h-4 bp:w-4 text-gray-500 dark:text-gray-400 transition-transform ${
                showLanguageOptions ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {/* Language Options Dropdown */}
          {showLanguageOptions && (
            <div 
              className="absolute top-full left-0 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {LANGUAGES.map((language) => (
                <div
                  key={language.code}
                  className={`flex items-center gap-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                    selectedLanguage.code === language.code
                      ? "bg-gray-100 dark:bg-gray-700"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleLanguageChange(language);
                  }}
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-full overflow-hidden">
                    {language.Flag ? <language.Flag /> : <CountryIcon />}
                  </div>
                  <span className="text-[9px] bp:text-sm lg:text-base dark:text-white">
                    {language.name} ({language.country})
                  </span>
                  {language.dir === "rtl" && (
                    <span className="text-[8px] bp:text-xs text-gray-500 dark:text-gray-400 ml-auto">
                      RTL
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CurrencyDropdown />
    </div>
  );
};

const CountryDropdown = () => {
  const selectedCountry = useAppSelector(
    (state) => state.locale.selectedCountry,
  );
console.log("Selected Country in Dropdown:", selectedCountry);
  const [showCountryOptions, setShowCountryOptions] = useState(false);
  const country = useAppSelector((state) => {
    return state.locale.country;
  });
  const dispatch = useAppDispatch();

  return (
    <div className="w-full">
      <h3 className="text-base font-medium mb-4 dark:text-white">Country</h3>

      {/* Country Selection */}
      <div className="mb-4 relative">
        <div
          className="flex items-center justify-between p-2 bp:p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer transition duration-150 ease-in-out hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={(e) => {
            e.stopPropagation();
            setShowCountryOptions(!showCountryOptions);
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[9px] bp:text-sm lg:text-base dark:text-white">
              {selectedCountry.name} ({selectedCountry.symbol})
            </span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-2 w-2 bp:h-4 bp:w-4 text-gray-500 dark:text-gray-400 transition-transform ${
              showCountryOptions ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        {/* Country Options Dropdown */}
        {showCountryOptions && (
          <div 
            className="absolute top-full left-0 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-20 max-h-48 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {country.items.map((count) => (
              <div
                key={count.id}
                className={`flex items-center gap-2 p-3 transition duration-100 cursor-pointer ${
                  selectedCountry.id === count.id
                    ? "bg-indigo-50 dark:bg-indigo-800 text-indigo-600 dark:text-white font-medium"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setSelectedCountry(count));
                  setShowCountryOptions(false);
                }}
              >
                <span className="text-[9px] bp:text-sm lg:text-base">
                  {count.name} ({count.symbol})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CurrencyDropdown = () => {
  const selectedCurrency = useAppSelector(
    (state) => state.locale.selectedCurrency,
  );

  const [showCurrencyOptions, setShowCurrencyOptions] = useState(false);
  const currency = useAppSelector((state) => state.locale.currency);
  const dispatch = useAppDispatch();

  return (
    <div className="w-full">
      <h3 className="text-base font-medium mb-4 dark:text-white">Currency</h3>

      {/* Currency Selection */}
      <div className="mb-4 relative">
        <div
          className="flex items-center justify-between p-2 bp:p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer transition duration-150 ease-in-out hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={(e) => {
            e.stopPropagation();
            setShowCurrencyOptions(!showCurrencyOptions);
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-[9px] bp:text-sm lg:text-base dark:text-white font-medium">
              {selectedCurrency.name} ({selectedCurrency.symbol})
            </span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-2 w-2 bp:h-4 bp:w-4 text-gray-500 dark:text-gray-400 transition-transform ${
              showCurrencyOptions ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        {/* Currency Options Dropdown */}
        {showCurrencyOptions && (
          <div 
            className="absolute top-full left-0 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-20 max-h-48 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {currency.items.map((cur) => (
              <div
                key={cur.symbol}
                className={`flex items-center gap-2 p-3 transition duration-100 cursor-pointer ${
                  selectedCurrency.symbol === cur.symbol
                    ? "bg-green-50 dark:bg-green-800 text-green-600 dark:text-white font-medium"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setSelectedCurrency(cur));
                  setShowCurrencyOptions(false);
                }}
              >
                <span className="text-[9px] bp:text-sm lg:text-base">
                  {cur.name} ({cur.symbol})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};